import { motion } from 'framer-motion';
import { Calendar, MapPin, Plus } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { typography } from '../../styles/typography';
import { Card } from '../ui';
import { stockOutApi } from './stockOutApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const StockOutView = () => {
  // Log screen view
  useScreenLogging('StockOutView', {
    component: 'StockOutView',
    screenType: 'view-stock-out',
  });

  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Always start with current month
    return getCurrentMonth();
  });
  const [selectedDate, setSelectedDate] = useState('');
  const [sites, setSites] = useState([]); // Always initialize as empty array
  const [stockOutEntries, setStockOutEntries] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  // Fetch sites on mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsLoadingSites(true);
      try {
        const sitesData = await stockOutApi.getSites();
        let sitesList = [];
        
        if (Array.isArray(sitesData)) {
          sitesList = sitesData;
        } else if (sitesData) {
          sitesList = Array.isArray(sitesData.sites) 
            ? sitesData.sites 
            : (Array.isArray(sitesData.data) ? sitesData.data : []);
        }
        
        // Ensure sitesList is always an array
        if (!Array.isArray(sitesList)) {
          console.warn('StockOutView: Sites data is not an array, defaulting to empty array');
          sitesList = [];
        }
        
        // Sort sites alphabetically by name
        const sortedSites = [...sitesList].sort((a, b) => {
          const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
          const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setSites(sortedSites);
        // Don't auto-select - let user select from dropdown
      } catch (error) {
        console.error('Error fetching sites:', error);
        setError('Failed to load sites. Please refresh the page.');
        setSites([]); // Ensure sites is always an array even on error
      } finally {
        setIsLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  // Function to fetch entries (can be called from multiple places)
  const fetchStockOutEntries = useCallback(async () => {
    if (!selectedSite) {
      setStockOutEntries([]);
      return;
    }

    setIsLoadingEntries(true);
    setError('');
    
    try {
      console.log('StockOutView: Fetching entries for:', {
        siteId: selectedSite.site_id || selectedSite.id,
        month: selectedMonth,
        date: selectedDate,
      });

      const result = await stockOutApi.getStockOutEntries({
        siteId: selectedSite.site_id || selectedSite.id,
        month: selectedMonth,
        limit: 100,
      });

      if (result.success) {
        const entries = result.entries || [];
        console.log('StockOutView: Fetched entries:', entries.length);
        console.log('StockOutView: First entry sample:', entries[0]);
        setStockOutEntries(entries);
        
        // Also set summary if available
        if (result.summary) {
          setSummary(result.summary);
        }
      } else {
        setError(result.message || 'Failed to load stock-out entries');
        setStockOutEntries([]);
      }
    } catch (error) {
      console.error('Error fetching stock-out entries:', error);
      setError('Failed to load stock-out entries. Please try again.');
      setStockOutEntries([]);
    } finally {
      setIsLoadingEntries(false);
    }
  }, [selectedSite, selectedMonth, selectedDate]);

  // Fetch entries when site or month changes
  useEffect(() => {
    fetchStockOutEntries();
  }, [fetchStockOutEntries]);

  const navigate = useNavigate();

  // Filter data by site and month
  const filteredRows = useMemo(() => {
    if (!selectedSite) return [];
    return stockOutEntries.filter((row) => {
      // Try multiple possible date field names
      const rowDateValue = row.stock_date || row.event_date || row.date || row.stockDate;
      if (!rowDateValue) {
        console.warn('StockOutView: Entry missing date field:', row);
        return false;
      }
      
      const rowDate = new Date(rowDateValue);
      if (isNaN(rowDate.getTime())) {
        console.warn('StockOutView: Invalid date value:', rowDateValue);
        return false;
      }
      
      const [year, month] = selectedMonth.split('-');
      const matchesMonth =
        rowDate.getFullYear() === parseInt(year) &&
        rowDate.getMonth() + 1 === parseInt(month);
      const matchesSite = (selectedSite.site_id || selectedSite.id) === (row.site_id || row.siteId);
      return matchesMonth && matchesSite;
    });
  }, [selectedMonth, selectedSite, stockOutEntries]);

  // Group filtered rows by date
  const groupedByDate = useMemo(() => {
    const grouped = {};
    filteredRows.forEach((row) => {
      // Try multiple possible date field names and normalize to YYYY-MM-DD format
      const dateValue = row.stock_date || row.event_date || row.date || row.stockDate;
      if (!dateValue) return;
      
      // Normalize date to YYYY-MM-DD format for grouping
      const dateObj = new Date(dateValue);
      if (isNaN(dateObj.getTime())) return;
      
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const normalizedDate = `${year}-${month}-${day}`;
      
      if (!grouped[normalizedDate]) {
        grouped[normalizedDate] = [];
      }
      grouped[normalizedDate].push(row);
    });
    return grouped;
  }, [filteredRows]);

  // Get dates sorted
  const dates = useMemo(() => {
    return Object.keys(groupedByDate).sort((a, b) => new Date(b) - new Date(a));
  }, [groupedByDate]);

  // Calculate totals for a date
  const getDateTotals = (date) => {
    const items = groupedByDate[date] || [];
    const totalItems = items.length;
    const totalCost = items.reduce((sum, item) => {
      return sum + ((item.quantityOrWeight || item.quantity || 0) * (item.pricePerKg || item.price || 0));
    }, 0);
    return { totalItems, totalCost };
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header with Title and Create Button */}
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={typography.h2} style={{ color: '#000000' }}>
                Stock Out
              </h1>
              <p className="mt-2 text-gray-600">
                View and manage stock-out entries
              </p>
            </div>
            <button
              onClick={() => navigate('/onboarding-stock-out/create')}
              className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              <Plus className="w-4 h-4" />
              Create Stock Out
            </button>
          </div>

          <div className="rounded-3xl">
            {/* Site and Month Selection */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <div className="min-w-[200px]">
                <label htmlFor="stock-out-site" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <MapPin className="w-4 h-4" />
                  Select Site
                </label>
                <select
                  id="stock-out-site"
                  value={selectedSite ? (selectedSite.site_id || selectedSite.id) : ''}
                  onChange={(e) => {
                    const siteId = e.target.value;
                    const site = sites.find(s => (s.site_id || s.id) === siteId);
                    setSelectedSite(site || null);
                  }}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  required
                >
                  <option value="">
                    {isLoadingSites ? 'Loading sites...' : 'Select a site'}
                  </option>
                  {/* alphabetical order */}
                  {Array.isArray(sites) && sites.map((site) => (
                    <option key={site.site_id || site.id} value={site.site_id || site.id}>
                      {site.site_name || site.name || 'Unknown Site'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[200px]">
                <label htmlFor="stock-out-month" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <Calendar className="w-4 h-4" />
                  Select Month
                </label>
                <input
                  id="stock-out-month"
                  type="month"
                  value={selectedMonth}
                  onChange={(event) => {
                    console.log('StockOutView: Month changed to:', event.target.value);
                    setSelectedMonth(event.target.value);
                  }}
                  className="w-full h-12 rounded-2xl border border-gray-200 bg-white px-4 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Current: {getCurrentMonth()} | Selected: {selectedMonth}
                </p>
              </div>
            </div>

            {/* Stock Out Cards - Show Each Entry Separately */}
            <div className="space-y-4">
              {error && (
                <Card.Light className="p-4 bg-red-50 border border-red-200">
                  <p className="text-sm text-red-800">{error}</p>
                </Card.Light>
              )}
              {isLoadingEntries ? (
                <Card.Light className="p-8 text-center">
                  <p className="text-gray-500 text-lg">Loading stock-out entries...</p>
                </Card.Light>
              ) : filteredRows.length > 0 ? (
                <>
                  {filteredRows.map((entry, index) => {
                    // Extract date from entry
                    const dateValue = entry.stock_date || entry.event_date || entry.date || entry.stockDate;
                    const entryDate = dateValue ? new Date(dateValue).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    }) : 'Unknown Date';
                    
                    // Get entry ID
                    const entryId = entry.stock_out_record_id || 
                                  entry.stock_out_id ||
                                  entry.ledger_id ||
                                  entry.id;
                    
                    // Calculate cost for this entry (only for "Sold" items)
                    const quantity = entry.quantity || entry.qty || entry.quantityOrWeight || 0;
                    const reason = entry.reason || '';
                    const isSold = reason === 'Sold' || reason === 'Sell';
                    const price = isSold ? (entry.selling_price || entry.pricePerKg || entry.price || 0) : 0;
                    const entryCost = isSold ? (quantity * price) : 0;
                    
                    // Get item name
                    const itemName = entry.item_name || entry.itemName || entry.stock_item_name || 'Unknown Item';
                    
                    return (
                      <motion.div
                        key={entryId || `${dateValue}-${index}`}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <Card.Light
                          className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                          onClick={() => {
                            // Navigate directly to edit page (not view page)
                            const ledgerId = entryId || 
                                           entry.ledger_id || 
                                           entry.stock_out_record_id || 
                                           entry.stock_out_id || 
                                           entry.id ||
                                           entry.ledger_entry_id;
                            
                            const editMode = !!ledgerId;
                            
                            navigate('/onboarding-stock-out/create', {
                              state: {
                                date: dateValue,
                                siteId: entry.site_id || entry.siteId,
                                siteName: entry.site_name || entry.siteName,
                                entryId: ledgerId,
                                entry: entry,
                                items: [{
                                  ledger_id: ledgerId,
                                  stock_item_id: entry.stock_item_id || entry.item_id || entry.stockItemId,
                                  item_id: entry.stock_item_id || entry.item_id || entry.stockItemId,
                                  itemName: itemName,
                                  quantity: quantity,
                                  quantityOrWeight: quantity,
                                  pricePerKg: price,
                                  selling_price: price,
                                  reason: entry.reason || '',
                                  action: entry.reason === 'Sold' ? 'Sell' : 'Return',
                                  notes: entry.notes || '',
                                  expDate: entry.exp_date || entry.expDate || '',
                                  vendorName: entry.vendor_name || entry.vendorName || '',
                                }],
                                editMode
                              }
                            });
                          }}
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">
                                  {index + 1}
                                </div>
                                <h3 className="text-xl font-semibold text-gray-800">
                                  {entryDate}
                                </h3>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-600">Item: </span>
                                  <span className="font-medium">{itemName}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Quantity: </span>
                                  <span className="font-medium">{quantity} {entry.unit_of_measure || entry.unit || ''}</span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Reason: </span>
                                  <span className="font-medium">{entry.reason || 'N/A'}</span>
                                </div>
                                {/* Only show cost for "Sold" items */}
                                {isSold && (
                                  <div>
                                    <span className="text-gray-600">Cost: </span>
                                    <span className="font-medium text-green-600">
                                      ₹{entryCost.toFixed(2)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                          </div>
                        </Card.Light>
                      </motion.div>
                    );
                  })}
                </>
              ) : (
                <Card.Light className="p-8 text-center">
                  <p className="text-gray-500 text-lg">
                    {!selectedSite
                      ? 'Please select a site to view stock-out entries.'
                      : 'No stock-out entries found for the selected filters.'}
                  </p>
                </Card.Light>
              )}
            </div>
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default StockOutView;


